<template>
  <div
    :class="[
      'group/collapsible-panel',
      { 'bg-gray-10': !isCollapsed && collapseToggleFull, 'mb-2 rounded-md hover:bg-gray-10': collapseToggleFull },
      panelClass,
      !isCollapsed && panelExpandedClass,
    ]"
  >
    <component
      :is="triggerAttrs.is"
      :class="[
        'group/collapsible-panel-button text-gray-60',
        { 'cursor-default': disabled, 'text-gray-80': collapseToggleFull, 'w-full': fullWidth || collapseToggleFull },
        hasAppendix && 'bg-zblue-10 hover:bg-zblue-10',
        titleLink ? 'p-0' : 'p-2',
        showLeftChevronIcon && 'pl-1',
      ]"
      :type="triggerAttrs.type"
      :aria-expanded="triggerAttrs.ariaExpanded"
      @click="collapse"
    >
      <template v-if="$slots.appendix"><slot name="appendix" /></template>
      <div :class="['flex w-full items-center gap-2', { 'justify-between': collapseToggleFull }]">
        <slot name="trigger">
          <span v-if="showLeftChevronIcon" :class="[chevronClasses, '-mr-2']">
            <chevron-down-icon :class="chevronIconClasses" />
          </span>
          <span
            v-if="$slots.icon || $slots.title"
            :class="[
              collapseToggleFull ? 'flex items-center gap-2.5 overflow-hidden text-xs' : 'text-xxs',
              titleLink ? '!inline-flex !items-center !gap-2 !text-sm content-link' : 'text-default font-bold',
            ]"
          >
            <span v-if="$slots.icon" class="flex flex-shrink-0 items-center">
              <slot name="icon" />
            </span>
            <template v-if="$slots.title">
              <slot name="title" />
            </template>
          </span>
          <div
            v-if="$slots.customContent"
            :class="customContentClass || 'flex w-full items-center justify-between overflow-x-hidden text-left'"
          >
            <slot name="customContent" />
          </div>
          <div class="flex items-center gap-2">
            <slot name="action-menu" :panel-collapsed="isCollapsed" />
            <span v-if="!disabled && !hideChevronIcon && chevronPosition === 'right'" :class="chevronClasses">
              <chevron-down-icon :class="chevronIconClasses" />
            </span>
          </div>
        </slot>
      </div>
      <template v-if="$slots.dispute">
        <slot name="dispute" />
      </template>
      <template v-if="$slots.evaluation">
        <slot name="evaluation" />
      </template>
    </component>
    <template v-if="$slots.rootCause">
      <slot name="rootCause" />
    </template>
    <div
      :class="[
        'opacity-1 flex max-h-[9999999999px] flex-col pb-2 transition-all duration-[600ms]',
        isCollapsed ? 'mt-0 !max-h-0 overflow-hidden !pb-0 opacity-0' : classList,
      ]"
    >
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ChevronDownIcon } from '@klausapp/ui-kit/icons'
import { ref, computed, watch } from 'vue'

const props = defineProps<{
  collapsed?: boolean
  disabled?: boolean
  collapseToggleFull?: boolean
  contentClass?: string
  customContentClass?: string
  panelClass?: string
  panelExpandedClass?: string
  hasAppendix?: boolean
  hideChevronIcon?: boolean
  chevronIconPosition?: 'left' | 'right'
  fullWidth?: boolean
  titleLink?: boolean
}>()

const emit = defineEmits<{
  (e: 'toggle'): void
}>()

const isCollapsed = ref(props.collapsed)
const showLeftChevronIcon = computed(
  () => props.chevronIconPosition === 'left' && !props.disabled && !props.hideChevronIcon,
)

const triggerAttrs = computed(() =>
  props.disabled
    ? { is: 'div', type: undefined, ariaExpanded: undefined }
    : { is: 'button', type: 'button', ariaExpanded: !isCollapsed.value },
)

const classList = computed(() => {
  if (props.contentClass) return props.contentClass
  return props.collapseToggleFull ? 'mt-2' : 'mt-3'
})

function collapse() {
  if (props.disabled) return

  isCollapsed.value = !isCollapsed.value
  emit('toggle')
}

const chevronPosition = computed(() => props.chevronIconPosition || 'right')

const chevronClasses = computed(() => [
  'flex items-center justify-center p-1',
  {
    'rotate-180': !isCollapsed.value,
    'rounded-full leading-[0] mix-blend-multiply': props.collapseToggleFull,
  },
])

const chevronIconClasses = computed(() => (props.titleLink ? 'text-gray-60 icon-m' : 'text-gray-60 icon-s'))

watch(
  () => props.collapsed,
  (value) => (isCollapsed.value = value),
)
</script>
